import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Wifi as ConnectedIcon,
  WifiOff as DisconnectedIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useConnectionStatus } from '../hooks/useRealTimeData';

interface ConnectionStatusProps {
  variant?: 'chip' | 'icon' | 'detailed';
  size?: 'small' | 'medium' | 'large';
  showLastUpdate?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  variant = 'chip',
  size = 'small',
  showLastUpdate = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const status = useConnectionStatus();

  const getStatusInfo = () => {
    if (!status.isConnected) {
      return {
        color: 'error' as const,
        label: 'Desconectado',
        icon: <DisconnectedIcon />,
        description: 'Sin conexión en tiempo real'
      };
    }

    const timeSinceHeartbeat = Date.now() - status.lastHeartbeat;
    if (timeSinceHeartbeat > 30000) { // 30 seconds
      return {
        color: 'warning' as const,
        label: 'Conexión lenta',
        icon: <SyncIcon className="rotating" />,
        description: 'Conectado, pero actualizaciones pueden estar retrasadas'
      };
    }

    return {
      color: 'success' as const,
      label: 'Conectado',
      icon: <ConnectedIcon />,
      description: 'Recibiendo datos en tiempo real'
    };
  };

  const formatLastUpdate = () => {
    if (!status.lastHeartbeat) return 'Nunca';
    
    const timeSince = Date.now() - status.lastHeartbeat;
    if (timeSince < 10000) return 'Ahora';
    if (timeSince < 60000) return `${Math.floor(timeSince / 1000)}s`;
    return `${Math.floor(timeSince / 60000)}m`;
  };

  const statusInfo = getStatusInfo();

  if (variant === 'icon') {
    return (
      <Tooltip title={statusInfo.description}>
        <Box
          sx={{
            color: `${statusInfo.color}.main`,
            display: 'flex',
            alignItems: 'center',
            '& .rotating': {
              animation: 'spin 2s linear infinite'
            },
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' }
            }
          }}
        >
          {React.cloneElement(statusInfo.icon, { 
            fontSize: size === 'large' ? 'large' : size === 'medium' ? 'medium' : 'small'
          })}
        </Box>
      </Tooltip>
    );
  }

  if (variant === 'detailed') {
    return (
      <Box
        sx={{
          p: isMobile ? 1 : 2,
          bgcolor: `${statusInfo.color}.light`,
          borderRadius: 1,
          border: `1px solid ${theme.palette[statusInfo.color].main}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box
          sx={{
            color: `${statusInfo.color}.main`,
            '& .rotating': {
              animation: 'spin 2s linear infinite'
            },
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' }
            }
          }}
        >
          {statusInfo.icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={`${statusInfo.color}.main`}
            sx={{ fontSize: isMobile ? '0.8rem' : undefined }}
          >
            {statusInfo.label}
          </Typography>
          {showLastUpdate && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ 
                fontSize: isMobile ? '0.7rem' : undefined,
                display: 'block'
              }}
            >
              Última actualización: {formatLastUpdate()}
            </Typography>
          )}
        </Box>
        {status.reconnectAttempts > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
          >
            Intentos: {status.reconnectAttempts}
          </Typography>
        )}
      </Box>
    );
  }

  // Default chip variant
  return (
    <Tooltip title={showLastUpdate ? `${statusInfo.description} • Última actualización: ${formatLastUpdate()}` : statusInfo.description}>
      <Chip
        icon={statusInfo.icon}
        label={isMobile ? statusInfo.label.substring(0, 8) : statusInfo.label}
        color={statusInfo.color}
        size={size}
        sx={{
          fontSize: isMobile ? '0.75rem' : undefined,
          '& .MuiChip-icon .rotating': {
            animation: 'spin 2s linear infinite'
          },
          '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' }
          }
        }}
      />
    </Tooltip>
  );
};

export default ConnectionStatus;